import type {
  FormField,
  FormComponentType,
  GroupField,
  FormRowField,
} from '@components/pages/FormBuilder/types'
import type { IFormTemplate } from '@dataTypes/forms.type'
import type { FormData } from '@components/pages/FormBuilder/PendingFormsTable/CompletedFormsModal.component'
import type {
  SchemaFormFieldValue,
  SchemaFormFieldValues,
} from '@components/pages/FormBuilder/Shared/SchemaFormRenderer'
import {
  choiceOptionsForField,
  normalizeLoadedGroups,
} from '@components/pages/FormBuilder/createFormField'

type FormDataItem = FormData['fields'][number]['items'][number]

function groupIntoRenderUnits(
  items: FormDataItem[],
): Array<
  | { kind: 'row'; items: FormDataItem[]; startIndex: number }
  | { kind: 'single'; item: FormDataItem; index: number }
> {
  const units: Array<
    | { kind: 'row'; items: FormDataItem[]; startIndex: number }
    | { kind: 'single'; item: FormDataItem; index: number }
  > = []
  let i = 0
  while (i < items.length) {
    const item = items[i]
    if (item.rowGroup !== undefined) {
      const start = i
      while (i < items.length && items[i]!.rowGroup === item.rowGroup) {
        i++
      }
      units.push({
        kind: 'row',
        items: items.slice(start, i),
        startIndex: start,
      })
    } else {
      units.push({ kind: 'single', item, index: i })
      i++
    }
  }
  return units
}

/** Common label substrings that indicate a date field when isDate is not set (e.g. API/mock data). */
const DATE_LABEL_PATTERNS =
  /date|birth|dob|witness date|consent date|appointment date/i

function itemTypeFromItem(item: FormDataItem): FormComponentType {
  if (item.isCheckboxGroup) return 'Checkbox Group'
  if (item.isDropdown) return 'Dropdown'
  if (item.isTextArea) return 'Text Area'
  if (item.isCheckbox) return 'Checkbox'
  if (item.isRichText) return 'Rich Text'
  if (item.isDate) return 'Date Field'
  if (item.isAttachments) return 'Attachments'
  if (item.options) return 'Radio Group'
  const label = (item.label ?? '').toLowerCase()
  if (label.includes('signature')) return 'Signature Field'
  // Fallback when FormData has no type flags (e.g. from API or mock): infer from value/label
  const value = item.value ?? ''
  if (
    typeof value === 'string' &&
    (value.includes('\n') || value.length > 150)
  ) {
    return 'Rich Text'
  }
  if (DATE_LABEL_PATTERNS.test(item.label ?? '')) return 'Date Field'
  if (label.includes('attachment') || label.includes('document')) {
    return 'Attachments'
  }
  return 'Input Field'
}

function itemValueForFieldValues(item: FormDataItem): SchemaFormFieldValue {
  if (item.isCheckboxGroup) return item.selectedOptions ?? []
  if (item.isCheckbox) return item.checked ?? false
  if (item.options && item.selected != null) return item.selected
  return item.value ?? ''
}

function fieldFromItem(id: string, item: FormDataItem): FormField {
  const type = itemTypeFromItem(item)
  return {
    id,
    type,
    label: item.label ?? '',
    ...(item.options ? { options: item.options } : {}),
  }
}

/**
 * Converts legacy FormData (section/items) to schema-native shape for SchemaFormRenderer.
 * Uses synthetic group and field ids so completed-form modal/page can render with SchemaFormRenderer.
 */
export function formDataToSchemaEntry(form: FormData): {
  groups: GroupField[]
  fieldValues: SchemaFormFieldValues
} {
  const fieldValues: SchemaFormFieldValues = {}
  const groups: GroupField[] = form.fields.map((section, sectionIdx) => {
    const items = section.items
    const renderUnits = groupIntoRenderUnits(items)
    const fields: (FormField | FormRowField)[] = renderUnits.map(
      (unit, unitIdx) => {
        if (unit.kind === 'row') {
          const rowFields: FormField[] = unit.items.map((item, colIdx) => {
            const id = `g${sectionIdx}-r${unitIdx}-f${colIdx}`
            fieldValues[id] = itemValueForFieldValues(item)
            return fieldFromItem(id, item)
          })
          return {
            id: `g${sectionIdx}-r${unitIdx}`,
            type: 'Form Row' as const,
            fields: rowFields,
          }
        }
        const item = unit.item
        const id = `g${sectionIdx}-f${unit.index}`
        fieldValues[id] = itemValueForFieldValues(item)
        return fieldFromItem(id, item)
      },
    )
    return {
      id: `g-${sectionIdx}`,
      name: section.section,
      fields,
      required: false,
      autoFill: false,
    }
  })
  return { groups, fieldValues }
}

/**
 * Parses schema_json (string or array) to GroupField[] for schema-native rendering.
 * Accepts template or kiosk API response shape.
 */
export interface SchemaJsonToGroupsResult {
  groups: GroupField[]
  error: string | null
}

export function schemaJsonToGroupsWithError(
  schemaJson:
    | IFormTemplate['schema_json']
    | string
    | Record<string, unknown>
    | unknown[]
    | null
    | undefined,
): SchemaJsonToGroupsResult {
  if (schemaJson == null) {
    return { groups: [], error: null }
  }
  try {
    const rawSchema =
      typeof schemaJson === 'string'
        ? (JSON.parse(schemaJson) as GroupField[] | { groups?: GroupField[] })
        : (schemaJson as GroupField[] | { groups?: GroupField[] })
    const raw = Array.isArray(rawSchema) ? rawSchema : rawSchema.groups
    if (!Array.isArray(raw)) {
      return {
        groups: [],
        error: 'Invalid form schema format: expected groups array.',
      }
    }
    return { groups: normalizeLoadedGroups(raw), error: null }
  } catch {
    return { groups: [], error: 'Invalid form schema JSON.' }
  }
}

export function schemaJsonToGroups(
  schemaJson:
    | IFormTemplate['schema_json']
    | string
    | Record<string, unknown>
    | unknown[]
    | null
    | undefined,
): GroupField[] {
  return schemaJsonToGroupsWithError(schemaJson).groups
}

function isGroupField(
  f: FormField | GroupField | FormRowField,
): f is GroupField {
  return (
    'name' in f &&
    'fields' in f &&
    !('type' in f && (f as FormRowField).type === 'Form Row')
  )
}

function isFormRowField(
  f: FormField | GroupField | FormRowField,
): f is FormRowField {
  return 'type' in f && (f as FormRowField).type === 'Form Row'
}

function fieldToItems(
  field: FormField | GroupField | FormRowField,
): FormDataItem[] {
  if (isFormRowField(field)) {
    return field.fields.flatMap((f) => fieldToItems(f))
  }
  if (isGroupField(field)) {
    return field.fields.flatMap((f) => fieldToItems(f))
  }
  const formField = field as FormField
  const label = formField.label ?? ''
  const base = { label, value: null as string | null }
  switch (formField.type) {
    case 'Gender Selection':
    case 'Radio Group':
      return [{ ...base, options: choiceOptionsForField(formField) }]
    case 'Dropdown':
      return [
        {
          ...base,
          isDropdown: true,
          options: choiceOptionsForField(formField),
        },
      ]
    case 'Checkbox Group':
      return [
        {
          ...base,
          isCheckboxGroup: true,
          options: choiceOptionsForField(formField),
          selectedOptions: [],
        },
      ]
    case 'Checkbox':
      return [{ ...base, isCheckbox: true, checked: false }]
    case 'Text Area':
      return [{ ...base, isTextArea: true }]
    case 'Rich Text':
      return [{ ...base, isRichText: true }]
    case 'Date Field':
      return [{ ...base, isDate: true }]
    case 'Attachments':
      return [{ ...base, isAttachments: true }]
    default:
      return [base]
  }
}

function groupsToFormDataFields(groups: GroupField[]): FormData['fields'] {
  return groups.map((group) => ({
    section: group.name,
    items: group.fields.flatMap((f) => fieldToItems(f)),
  }))
}

/**
 * Builds FormData.fields from schema groups and a field-id value map (e.g. for kiosk submit payload).
 */
export function formDataFieldsFromSchemaAndValues(
  groups: GroupField[],
  fieldValues: SchemaFormFieldValues,
): FormData['fields'] {
  let rowGroupCounter = 0

  function fieldToItemsWithValues(
    field: FormField | GroupField | FormRowField,
    rowGroup?: number,
  ): FormDataItem[] {
    if (isFormRowField(field)) {
      const currentRowGroup = rowGroupCounter++
      return field.fields.flatMap((f) =>
        fieldToItemsWithValues(f, currentRowGroup),
      )
    }
    if (isGroupField(field)) {
      return field.fields.flatMap((f) => fieldToItemsWithValues(f, rowGroup))
    }
    const formField = field as FormField
    const label = formField.label ?? ''
    const raw = fieldValues[formField.id]
    const value: string | null =
      typeof raw === 'string' ? raw || null : raw === true ? 'true' : null
    const base: FormDataItem = {
      label,
      value,
      ...(rowGroup !== undefined ? { rowGroup } : {}),
    }
    const selected = typeof raw === 'string' && raw.length > 0 ? raw : undefined
    const selectedOptions = Array.isArray(raw)
      ? raw.filter((item): item is string => typeof item === 'string')
      : []
    switch (formField.type) {
      case 'Gender Selection':
      case 'Radio Group':
        return [
          {
            ...base,
            options: choiceOptionsForField(formField),
            selected,
          },
        ]
      case 'Dropdown':
        return [
          {
            ...base,
            isDropdown: true,
            options: choiceOptionsForField(formField),
            selected,
          },
        ]
      case 'Checkbox Group':
        return [
          {
            ...base,
            value:
              selectedOptions.length > 0 ? selectedOptions.join(', ') : null,
            isCheckboxGroup: true,
            options: choiceOptionsForField(formField),
            selectedOptions,
          },
        ]
      case 'Text Area':
        return [{ ...base, isTextArea: true }]
      case 'Checkbox':
        return [
          {
            ...base,
            value: null,
            isCheckbox: true,
            checked: raw === true,
          },
        ]
      case 'Rich Text':
        return [{ ...base, isRichText: true }]
      case 'Date Field':
        return [{ ...base, isDate: true }]
      case 'Attachments':
        return [{ ...base, isAttachments: true }]
      default:
        return [base]
    }
  }
  return groups.map((group) => ({
    section: group.name,
    items: group.fields.flatMap((f) => fieldToItemsWithValues(f)),
  }))
}

/**
 * Converts an IFormTemplate to FormData (id, name, fields). Used for Assign Forms list payload
 * (createAssignedFormEntry only needs id/name; fields kept for compatibility). Assign modal
 * review step now uses schemaJsonToGroups + SchemaFormRenderer instead of this for display.
 */
export function formTemplateToFormData(template: IFormTemplate): FormData {
  const id = template.form_template_id ?? ''
  const name = template.title ?? 'Untitled Form'
  const { groups } = schemaJsonToGroupsWithError(template.schema_json)
  const fields = groupsToFormDataFields(groups)
  return { id, name, fields }
}
